import { useEffect, useState } from 'react';
import axios from 'axios';

import { entitlementsMine, entitlementsSubscribe, entitlementsCancel } from '../urls/api-shipments/entitlements';
import { getStoredRoles } from '../Auth/session';

// Fetches the signed-in merchant's resolved entitlement (tier + unlocked
// features) once and caches it process-wide. The dashboard reads this to drive
// lock badges, blurred previews, and Bill's free-tier lock.
let cache = null;
let inflight = null;

const authHeaders = () => ({
  Authorization: 'Bearer ' + (localStorage.getItem('access_token') || ''),
  'content-type': 'application/json'
});

// Admin / dispatch accounts (roles 0, 100) are internal staff and must never be
// gated. They bypass entitlements entirely with a fully-unlocked view.
const ADMIN_ROLES = [0, 100];
function isAdminUser() {
  try {
    const roles = getStoredRoles();
    return Array.isArray(roles) && roles.some(r => ADMIN_ROLES.includes(Number(r)));
  } catch (e) {
    return false;
  }
}
const ADMIN_ENTITLEMENT = {
  tier: 'enterprise',
  trial_active: false,
  trial_ends_at: null,
  feature_overrides: {},
  features: new Proxy({}, { get: () => true }),
};

export function fetchEntitlements(force) {
  if (isAdminUser()) return Promise.resolve(ADMIN_ENTITLEMENT);
  if (cache && !force) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = axios
    .get(entitlementsMine, { headers: authHeaders() })
    .then(res => {
      cache = (res && res.data && res.data.payload) || (res && res.data) || null;
      inflight = null;
      return cache;
    })
    .catch(() => {
      inflight = null;
      return null;
    });
  return inflight;
}

export function getCachedEntitlements() {
  return cache;
}

// Self-serve upgrade: charges the saved card/ACH for the chosen tier and, on
// success, returns the freshly resolved entitlement (and updates the cache so
// lock badges unlock immediately). Never throws — resolves { ok, message }.
export function subscribeTier(tier) {
  return axios
    .post(entitlementsSubscribe, { tier }, { headers: authHeaders() })
    .then(res => {
      const payload = (res && res.data && res.data.payload) || (res && res.data) || null;
      if (payload && payload.features) {
        cache = payload;
      }
      return { ok: true, entitlement: payload };
    })
    .catch(err => {
      const data = err && err.response && err.response.data;
      const message = (data && (data.message || data.error)) || 'Upgrade failed. Please check your payment method and try again.';
      return { ok: false, message };
    });
}

// Downgrade to Free: cancels the Stripe subscription and returns the resolved plan.
export function cancelSubscription() {
  return axios
    .post(entitlementsCancel, {}, { headers: authHeaders() })
    .then(res => {
      const payload = (res && res.data && res.data.payload) || (res && res.data) || null;
      if (payload && payload.features) {
        cache = payload;
      }
      return { ok: true, entitlement: payload };
    })
    .catch(err => {
      const data = err && err.response && err.response.data;
      const message = (data && (data.message || data.error)) || 'Could not change your plan. Please try again.';
      return { ok: false, message };
    });
}

// True only when we positively know the feature is locked; unknown/loading is
// treated as unlocked so paid users never see a flash of "locked".
export function isFeatureLocked(key) {
  if (isAdminUser()) return false;
  return Boolean(cache && cache.features && cache.features[key] === false);
}

export function useEntitlements() {
  const [ent, setEnt] = useState(isAdminUser() ? ADMIN_ENTITLEMENT : cache);
  useEffect(() => {
    if (isAdminUser()) {
      setEnt(ADMIN_ENTITLEMENT);
      return;
    }
    let active = true;
    fetchEntitlements().then(e => {
      if (active) setEnt(e);
    });
    return () => {
      active = false;
    };
  }, []);
  return ent;
}
